import React from 'react';
import { Box, Card, IconButton, Tooltip, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { useNavigate } from 'react-router-dom';
import { WIDGET_SIZES } from './sizes.js';
import { widgetHref } from './widgetRegistry.js';

/**
 * Elements inside a widget that must not start a drag in edit mode
 * (react-grid-layout `draggableCancel`). Add the class to anything interactive.
 */
export const WIDGET_NO_DRAG_CLASS = 'widget-no-drag';
export const DRAG_CANCEL_SELECTOR = `.${WIDGET_NO_DRAG_CLASS}, button, a, input, textarea, select, [role="button"]`;

/**
 * The card every widget renders in (Beta-1 plan P3-15).
 *
 * Header: title (a link to the page the widget stands for — "widgets are shortcuts", C0813),
 * settings gear when the widget has a settingsSchema, and, in edit mode, the ⊖ remove badge
 * on the top-left corner plus a size caption. Small widgets are clickable as a whole, like a
 * phone home-screen tile; larger ones only via the title (their bodies hold charts/lists).
 *
 * `widget.frameless` (P3-20): the component brings its own card and heading — no shell header;
 * in edit mode the size caption and gear float over the top-right corner. `widget.scroll`: the
 * body scrolls instead of clipping.
 *
 * Props
 *   widget         registry definition
 *   item           layout item ({ i, widgetId, props, … })
 *   size           current size key (S/M/L/XL) — in the grid's column space, see WidgetGrid
 *   editing        edit mode (P3-16): drag anywhere on the card, remove badge visible
 *   onRemove(i)    optional; badge is hidden when missing
 *   onOpenSettings(i) optional; gear is hidden when missing or the widget has no settings
 *   clickable      whole-card navigation (default: size === 'S')
 *   headerAction   optional extra node right of the title
 */
export function WidgetShell({
  widget,
  item,
  size,
  editing = false,
  onRemove,
  onOpenSettings,
  clickable,
  headerAction = null,
  children,
  sx,
}) {
  const navigate = useNavigate();
  const href = widgetHref(widget, item);
  const hasSettings = Boolean(onOpenSettings && widget.settingsSchema && widget.settingsSchema.length > 0);
  const cardClickable = !editing && Boolean(href) && (clickable ?? widget.clickable ?? size === 'S');
  const frameless = Boolean(widget.frameless);

  const go = (event) => {
    if (editing || !href) return;
    if (event) event.preventDefault();
    navigate(href);
  };

  return (
    <Card
      className={`widget-shell${editing ? ' widget-shell--editing' : ''}`}
      elevation={editing ? 4 : 1}
      onClick={cardClickable ? go : undefined}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
        cursor: editing ? 'grab' : cardClickable ? 'pointer' : 'default',
        userSelect: editing ? 'none' : undefined,
        transition: 'box-shadow 200ms ease, transform 200ms ease',
        ...(cardClickable && { '&:hover': { boxShadow: 4 } }),
        ...sx,
      }}
    >
      {editing && onRemove && (
        <Tooltip title={`Remove ${widget.title}`}>
          <IconButton
            className={`${WIDGET_NO_DRAG_CLASS} widget-remove`}
            size="small"
            aria-label={`Remove ${widget.title}`}
            onClick={(event) => {
              event.stopPropagation();
              onRemove(item.i);
            }}
            sx={{
              position: 'absolute',
              top: -12,
              left: -12,
              zIndex: 3,
              p: 0,
              bgcolor: 'background.paper',
              color: 'text.secondary',
              boxShadow: 2,
              '&:hover': { color: 'error.main', bgcolor: 'background.paper' },
            }}
          >
            <RemoveCircleIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* P3-18: frameless widgets get a hover-revealed gear outside edit mode */}
      {frameless && !editing && hasSettings && (
        <Tooltip title="Widget settings">
          <IconButton
            className={`${WIDGET_NO_DRAG_CLASS} widget-settings-hover`}
            size="small"
            aria-label={`${widget.title} settings`}
            onClick={(event) => {
              event.stopPropagation();
              onOpenSettings(item.i);
            }}
            sx={{ position: 'absolute', top: 6, right: 6, zIndex: 3, bgcolor: 'background.paper', boxShadow: 1, opacity: 0, transition: 'opacity 0.15s', '.widget-shell:hover &, &:focus-visible': { opacity: 1 }, '&:hover': { bgcolor: 'background.paper' } }}
          >
            <SettingsOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {frameless ? (
        editing && (
          <Box
            className="widget-header widget-header--overlay"
            sx={{ position: 'absolute', top: 4, right: 4, zIndex: 3, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, px: 0.5 }}
          >
            {size && WIDGET_SIZES[size] && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, whiteSpace: 'nowrap' }} aria-label="Widget size">
                {WIDGET_SIZES[size].label}
              </Typography>
            )}
            {headerAction}
            {hasSettings && (
              <IconButton className={WIDGET_NO_DRAG_CLASS} size="small" aria-label={`${widget.title} settings`} onClick={(event) => { event.stopPropagation(); onOpenSettings(item.i); }}>
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )
      ) : (
      <Box
        component="header"
        className="widget-header"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 2, pr: 1, pt: 1.25, pb: 0.25, minHeight: 40 }}
      >
        {href && !editing ? (
          <Typography
            component="a"
            href={href}
            onClick={(event) => {
              event.stopPropagation();
              go(event);
            }}
            variant="subtitle2"
            noWrap
            className={WIDGET_NO_DRAG_CLASS}
            sx={{
              flexGrow: 1,
              minWidth: 0,
              fontWeight: 700,
              color: 'text.primary',
              textDecoration: 'none',
              '&:hover, &:focus-visible': { color: 'primary.main', textDecoration: 'underline' },
            }}
          >
            {widget.title}
          </Typography>
        ) : (
          <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1, minWidth: 0, fontWeight: 700 }}>
            {widget.title}
          </Typography>
        )}
        {editing && size && WIDGET_SIZES[size] && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, whiteSpace: 'nowrap' }} aria-label="Widget size">
            {WIDGET_SIZES[size].label}
          </Typography>
        )}
        {headerAction}
        {hasSettings && (
          <Tooltip title="Widget settings">
            <IconButton
              className={WIDGET_NO_DRAG_CLASS}
              size="small"
              aria-label={`${widget.title} settings`}
              onClick={(event) => {
                event.stopPropagation();
                onOpenSettings(item.i);
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      )}

      <Box
        className="widget-body"
        sx={{
          flex: 1,
          minHeight: 0,
          px: frameless ? 0 : 2,
          pb: frameless ? 0 : 1.5,
          display: 'flex',
          flexDirection: 'column',
          overflow: widget.scroll ? 'auto' : 'hidden',
          // frameless widgets bring their own Card — let it fill the cell
          ...(frameless && { '& > .MuiCard-root, & > .MuiPaper-root': { flex: 1, minHeight: 0, boxShadow: 'none', borderRadius: 'inherit' } }),
          pointerEvents: editing ? 'none' : undefined, // in edit mode the card is a drag surface
        }}
      >
        {children}
      </Box>
    </Card>
  );
}

export default WidgetShell;
